import React, { useCallback } from 'react';
import { Field, Formik } from 'formik';
import _ from 'lodash';
import {
  BooleanToggleType,
  AllBooleanToggleTypes,
  CommunicationPreferencesUpdateRequest,
  AllElectronicFormats,
  AllDuplicateStatementOptions,
  DuplicateStatements,
  getGroupedCountryOptions,
  AllStates,
  Country,
  CommunicationPreferencesUpdateValues,
  DeliveryType,
  State,
} from '@tradingblock/types';
import { FormGroup, Modal, SelectDropdownField, StepperField, TextboxField } from '@tradingblock/components';
import { useStateSelector, apiTokenSelector } from '../../../data/global/dataSelectors';
import { useDispatch } from 'react-redux';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { getRequiredError, USPSErrors } from './Validation';
import { validateAddressFields } from './addressValidation';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { formatBoolean } from '../../../utilities/accountManagement';
import { useClearer } from '../../../hooks/useClearer';

export const CommunicationPreferencesUpdate: React.FunctionComponent<{
  show?: boolean;
  toggleModal: () => void;
}> = ({ show, toggleModal }) => {
  const dispatch = useDispatch();

  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const apiToken = useStateSelector(apiTokenSelector);
  const clearer = useClearer();

  const initialCommunicationPreferencesUpdateValues: CommunicationPreferencesUpdateRequest = {
    deliveryType: undefined,
    requestDuplicateStatements: undefined,
    duplicateStatements: undefined,
    duplicateStatementsAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    },
    duplicateStatementsEmail: undefined,
    preferredElectronicFormat: undefined,
    subscribeToPromotionalEmails: undefined,
  };

  const onValidate = (values: CommunicationPreferencesUpdateRequest) => {
    const storageKey = `addressVerification-duplicateStatementsAddress`;

    // Use shared address validation function
    const addressErrors = validateAddressFields(
      {
        address1: values.duplicateStatementsAddress && values.duplicateStatementsAddress.address1,
        address2: values.duplicateStatementsAddress && values.duplicateStatementsAddress.address2,
        city: values.duplicateStatementsAddress && values.duplicateStatementsAddress.city,
        state: values.duplicateStatementsAddress && values.duplicateStatementsAddress.state,
        postalCode: values.duplicateStatementsAddress && values.duplicateStatementsAddress.postalCode,
        country: values.duplicateStatementsAddress && values.duplicateStatementsAddress.country,
      },
      {
        fieldPrefix: 'duplicateStatementsAddress.',
        storageKey,
        apiToken,
        isMailing: false,
        triggerUspsVerification: true,
      }
    );

    // Merge address errors with other form validation errors
    const errors: USPSErrors = {
      ...addressErrors,
      [`deliveryType`]: getRequiredError(values.deliveryType),
      [`duplicateStatements`]:
        values.requestDuplicateStatements === BooleanToggleType.Yes
          ? getRequiredError(values.duplicateStatements)
          : undefined,
      [`preferredElectronicFormat`]: getRequiredError(values.preferredElectronicFormat),
      [`subscribeToPromotionalEmails`]: getRequiredError(values.subscribeToPromotionalEmails),
      [`requestDuplicateStatements`]: getRequiredError(values.requestDuplicateStatements),
      [`duplicateStatementsAddress.country`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.PostalMail
          ? values.duplicateStatementsAddress && values.duplicateStatementsAddress.country
          : true
      ),
      [`duplicateStatementsAddress.address1`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.PostalMail
          ? values.duplicateStatementsAddress && values.duplicateStatementsAddress.address1
          : true
      ),
      [`duplicateStatementsAddress.postalCode`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.PostalMail
          ? values.duplicateStatementsAddress && values.duplicateStatementsAddress.postalCode
          : true
      ),
      [`duplicateStatementsAddress.city`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.PostalMail
          ? values.duplicateStatementsAddress && values.duplicateStatementsAddress.city
          : true
      ),
      [`duplicateStatementsAddress.state`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.PostalMail
          ? values.duplicateStatementsAddress && values.duplicateStatementsAddress.state
          : true
      ),
      [`duplicateStatementsEmail`]: getRequiredError(
        values.duplicateStatements === DuplicateStatements.Email ? values.duplicateStatementsEmail : true
      ),
    };
    const cleanedErrors = _.omitBy(errors, _.isNil);
    return cleanedErrors;
  };

  const onSubmit = useCallback(
    (values, { resetForm }) => {
      // construct updateValues for submission, if duplicateStatementsAddress is not provided, then remove it from the updateValues same for duplicateStatementsEmail
      // set duplicateStatements to None if requestDuplicateStatements is No
      const updateValues: CommunicationPreferencesUpdateValues = {
        item: 'CommunicationPreferences',
        communicationPreferences: {
          deliveryType: values.deliveryType === BooleanToggleType.Yes ? DeliveryType.Paper : DeliveryType.Electronic,
          duplicateStatements: values.duplicateStatements,
          duplicateStatementsAddress: values.duplicateStatementsAddress,
          duplicateStatementsEmail: values.duplicateStatementsEmail,
          preferredElectronicFormat: values.preferredElectronicFormat,
          subscribeToPromotionalEmails: formatBoolean(values.subscribeToPromotionalEmails),
        },
      };

      // remove duplicateStatementsAddress and duplicateStatementsEmail if they are not provided
      // remove duplicateStatementsAddress if duplicateStatements is not PostalMail
      if (!values.duplicateStatementsAddress) {
        delete updateValues.communicationPreferences.duplicateStatementsAddress;
      }
      if (!values.duplicateStatementsEmail) {
        delete updateValues.communicationPreferences.duplicateStatementsEmail;
      }
      if (values.duplicateStatements !== DuplicateStatements.PostalMail) {
        delete updateValues.communicationPreferences.duplicateStatementsAddress;
      }
      if (values.requestDuplicateStatements === BooleanToggleType.No) {
        updateValues.communicationPreferences.duplicateStatements = DuplicateStatements.None;
      }

      dispatch(
        AccountManagementDataActions.requestMakeAccountUpdate({
          accountId,
          request: updateValues,
        })
      );
      resetForm();
      toggleModal();

      sessionStorage.setItem(`addressVerification-duplicateStatementsAddress`, 'undefined');
    },
    [toggleModal]
  );

  return (
    <Formik
      initialValues={initialCommunicationPreferencesUpdateValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnChange={true}
    >
      {({ handleSubmit, values, errors, resetForm, setValues, validateForm }) => (
        <Modal setShow={() => toggleModal()} show={show}>
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Communication Preferences Update</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <form onSubmit={handleSubmit}>
              <p>Would you like to receive paper confirmations and statements?</p>
              <FormGroup>
                <Field
                  id="deliveryType"
                  component={StepperField}
                  options={AllBooleanToggleTypes}
                  defaultLabelId="boolean"
                />
              </FormGroup>
              <p>Preferred electronic communications format?</p>
              <FormGroup>
                <Field
                  id="preferredElectronicFormat"
                  component={StepperField}
                  options={AllElectronicFormats}
                  defaultLabelId="electronicFormat"
                />
              </FormGroup>
              <p>Would you like to receive duplicate statements?</p>
              <FormGroup>
                <Field
                  id="requestDuplicateStatements"
                  component={StepperField}
                  options={AllBooleanToggleTypes}
                  defaultLabelId="booleanCommsPref"
                />
              </FormGroup>
              {values.requestDuplicateStatements === BooleanToggleType.Yes && (
                <>
                  <p>How would you like to receive duplicate statements?</p>
                  <FormGroup>
                    <Field
                      id="duplicateStatements"
                      component={StepperField}
                      options={AllDuplicateStatementOptions}
                      defaultLabelId="duplicateStatements"
                    />
                  </FormGroup>
                  {values.duplicateStatements === DuplicateStatements.Email && (
                    <FormGroup>
                      <Field id="duplicateStatementsEmail" component={TextboxField} label="Email" />
                    </FormGroup>
                  )}
                  {values.duplicateStatements === DuplicateStatements.PostalMail && (
                    <>
                      <p>Address for Duplicate Statements</p>
                      <FormGroup>
                        <Field
                          component={SelectDropdownField}
                          name={'duplicateStatementsAddress.country'}
                          id={'duplicateStatementsAddress.country'}
                          label="Country"
                          options={getGroupedCountryOptions(clearer)}
                          optionsDefaultLabelId="country"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field
                          id="duplicateStatementsAddress.address1"
                          name="duplicateStatementsAddress.address1"
                          component={TextboxField}
                          label="Address 1"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field
                          id="duplicateStatementsAddress.address2"
                          name="duplicateStatementsAddress.address2"
                          component={TextboxField}
                          label="Address 2"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Field
                          id="duplicateStatementsAddress.city"
                          name="duplicateStatementsAddress.city"
                          component={TextboxField}
                          label="City"
                        />
                      </FormGroup>
                      <FormGroup>
                        {values.duplicateStatementsAddress &&
                          values.duplicateStatementsAddress.country === Country.UnitedStatesOfAmerica && (
                            <Field
                              component={SelectDropdownField}
                              name={'duplicateStatementsAddress.state'}
                              id={'duplicateStatementsAddress.state'}
                              label="State"
                              options={AllStates}
                              optionsDefaultLabelId="state"
                            />
                          )}
                        {values.duplicateStatementsAddress &&
                          values.duplicateStatementsAddress.country !== Country.UnitedStatesOfAmerica &&
                          values.duplicateStatementsAddress.country !== '' && (
                            <Field component={TextboxField} name={'state'} id={'state'} label="Province" />
                          )}
                      </FormGroup>
                      <FormGroup>
                        <Field
                          id="duplicateStatementsAddress.postalCode"
                          name="duplicateStatementsAddress.postalCode"
                          component={TextboxField}
                          label="Postal Code"
                        />
                      </FormGroup>
                      {(() => {
                        // Get USPS response from storage
                        const uspsResponseFromStorage = sessionStorage.getItem('addressVerification-duplicateStatementsAddress');
                        let uspsResponseValues: any = null;
                        let uspsAddress1 = '';
                        let uspsAddress2 = '';
                        let uspsCity = '';
                        let uspsState = '';
                        let uspsPostalCode = '';

                        if (uspsResponseFromStorage && uspsResponseFromStorage !== 'undefined') {
                          try {
                            uspsResponseValues = JSON.parse(uspsResponseFromStorage);
                            if (uspsResponseValues && !uspsResponseValues.error) {
                              uspsAddress1 = uspsResponseValues.uspsAddress1 || '';
                              uspsAddress2 = uspsResponseValues.uspsAddress2 || '';
                              uspsCity = uspsResponseValues.city || '';
                              uspsState = uspsResponseValues.state || '';
                              const zip5 = uspsResponseValues.zip5 || '';
                              const zip4 = uspsResponseValues.zip4 || '';
                              uspsPostalCode = zip4 ? `${zip5}-${zip4}` : zip5;
                            }
                          } catch (e) {
                            // Invalid JSON, ignore
                          }
                        }

                        // Check if there are USPS formatting errors
                        const hasUspsErrors =
                          errors &&
                          ((uspsAddress1 && errors['duplicateStatementsAddress.address1'] && errors['duplicateStatementsAddress.address1'].includes('USPS provided formatting')) ||
                            (uspsAddress2 && errors['duplicateStatementsAddress.address2'] && errors['duplicateStatementsAddress.address2'].includes('USPS provided formatting')) ||
                            (uspsCity && errors['duplicateStatementsAddress.city'] && errors['duplicateStatementsAddress.city'].includes('USPS Provided')) ||
                            (uspsState && errors['duplicateStatementsAddress.state'] && errors['duplicateStatementsAddress.state'].includes('USPS Provided')) ||
                            (uspsPostalCode && errors['duplicateStatementsAddress.postalCode'] && errors['duplicateStatementsAddress.postalCode'].includes('USPS Provided')));

                        if (!hasUspsErrors || !uspsResponseValues) {
                          return null;
                        }

                        return (
                          <FormGroup>
                            <button
                              className="btn btn-block btn-outline-warning"
                              type="button"
                              onClick={e => {
                                e.preventDefault();
                                const updatedValues = { ...values };
                                if (!updatedValues.duplicateStatementsAddress) {
                                  updatedValues.duplicateStatementsAddress = {
                                    address1: '',
                                    address2: '',
                                    city: '',
                                    state: '',
                                    postalCode: '',
                                    country: '',
                                  };
                                }
                                if (uspsAddress1) updatedValues.duplicateStatementsAddress.address1 = uspsAddress1;
                                if (uspsAddress2) updatedValues.duplicateStatementsAddress.address2 = uspsAddress2;
                                else updatedValues.duplicateStatementsAddress.address2 = '';
                                if (uspsCity) updatedValues.duplicateStatementsAddress.city = uspsCity;
                                if (uspsState) updatedValues.duplicateStatementsAddress.state = uspsState;
                                if (uspsPostalCode) updatedValues.duplicateStatementsAddress.postalCode = uspsPostalCode;
                                setValues(updatedValues);
                                setTimeout(() => {
                                  validateForm();
                                }, 100);
                              }}
                            >
                              Use USPS provided address
                            </button>
                          </FormGroup>
                        );
                      })()}
                    </>
                  )}
                </>
              )}
              <p>Would you like to receive promotional emails?</p>
              <FormGroup>
                <Field
                  id="subscribeToPromotionalEmails"
                  component={StepperField}
                  options={AllBooleanToggleTypes}
                  defaultLabelId="boolean"
                />
              </FormGroup>
            </form>
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="modal-footer-justified">
            <Button
              variant="secondary"
              onClick={() => {
                resetForm();
                toggleModal();
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={(values: any) => handleSubmit(values)}>
              Submit
            </Button>
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
